import {
  addCurrentAuthHeader,
  type SessionService,
} from '@efdigital/ll-addon-auth';
import type {
  ImpersonationAuditListResponse,
  ImpersonationLinkCreateRequest,
  ImpersonationLinkCreateResponse,
} from '@efdigital/ll-cdk-auth-impersonation-api-types';

const IMPERSONATION_API_PREFIX = '/ll-auth-impersonation';

export class ImpersonationRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ImpersonationRequestError';
    this.status = status;
  }
}

export async function parseImpersonationErrorResponse(
  response: Response
): Promise<never> {
  let message = response.statusText || 'Request failed';

  try {
    const body = (await response.json()) as { message?: string };
    if (body.message) {
      message = body.message;
    }
  } catch {
    // Use statusText when body is not JSON.
  }

  throw new ImpersonationRequestError(message, response.status);
}

export async function buildCreateImpersonationLinkRequest(
  session: SessionService,
  body: ImpersonationLinkCreateRequest
): Promise<Request> {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${session.apis.systemsApi.host}${IMPERSONATION_API_PREFIX}/links`,
    init
  );
}

export async function parseCreateImpersonationLinkResponse(
  response: Response
): Promise<ImpersonationLinkCreateResponse> {
  if (!response.ok) {
    return parseImpersonationErrorResponse(response);
  }

  return (await response.json()) as ImpersonationLinkCreateResponse;
}

export async function buildListImpersonationAuditsRequest(
  session: SessionService,
  clientId?: string
): Promise<Request> {
  const url = new URL(
    `${session.apis.systemsApi.host}${IMPERSONATION_API_PREFIX}/audits`
  );
  if (clientId) {
    url.searchParams.set('clientId', clientId);
  }

  const init = await addCurrentAuthHeader(
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(url, init);
}

export async function parseListImpersonationAuditsResponse(
  response: Response
): Promise<ImpersonationAuditListResponse> {
  if (!response.ok) {
    return parseImpersonationErrorResponse(response);
  }

  return (await response.json()) as ImpersonationAuditListResponse;
}

export async function createImpersonationLink(
  session: SessionService,
  body: ImpersonationLinkCreateRequest
): Promise<ImpersonationLinkCreateResponse> {
  const request = await buildCreateImpersonationLinkRequest(session, body);
  const response = await fetch(request);
  return parseCreateImpersonationLinkResponse(response);
}

export async function listImpersonationAudits(
  session: SessionService,
  clientId?: string
): Promise<ImpersonationAuditListResponse> {
  const request = await buildListImpersonationAuditsRequest(session, clientId);
  const response = await fetch(request);
  return parseListImpersonationAuditsResponse(response);
}
